/**
 * Module dependencies,
 */

import { AppointmentFormData, postAppointment } from 'src/requests/appointment';
import { BookAppointmentForm } from 'src/components/forms/book-appointment';
import { Category, Service, SubCategory } from 'src/types/service';
import { FieldsGrid } from 'src/components/forms/shared/styles';
import { GetStaticProps, NextPage } from 'next';
import { Locale } from 'i18n-routes';
import { SEO } from 'src/components/core/seo';
import { SSGProps } from 'src/types/app';
import {
  SelectField,
  SelectFieldProps
} from 'src/components/core/forms/select/field';

import {
  ServicesBySubcategory,
  getService,
  getServicesBySubcategory
} from 'src/requests/service';

import { TriggerNavbarTheme } from 'src/components/layout/navbar/trigger-navbar-theme';
import { requiredRule } from 'src/core/forms/rules';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import {
  serviceAreaEnum,
  serviceCategoryEnum,
  serviceGenderEnum,
  serviceTypeEnum
} from 'src/core/constants/enums';

import { toast } from 'src/providers/toast';
import { useAsync } from 'react-async-hook';
import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useRouterHistory } from 'src/providers/route-history';
import { useTranslation } from 'next-i18next';
import { useWatch } from 'react-hook-form';
import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';

/**
 * `DynamicServiceSelectProps` type.
 */

type DynamicServiceSelectProps = Omit<SelectFieldProps, 'options' | 'value'> & {
  groupedServices?: ServicesBySubcategory;
};

/**
 * `subcategoryOptions` constant.
 */

const subcategoryOptions = {
  area: Object.keys(serviceAreaEnum),
  gender: Object.keys(serviceGenderEnum),
  type: Object.keys(serviceTypeEnum)
} as Record<Category, SubCategory[]>;

/**
 * `DynamicSubCategorySelect` component.
 */

export function DynamicSubCategorySelect(props: any) {
  const { control, ...selectProps } = props;
  const { t } = useTranslation('common');
  const category = useWatch({ control, name: 'serviceCategory' }) as Category;
  const options = useMemo(() => {
    const list = subcategoryOptions?.[category];

    if (!list?.length) {
      return undefined;
    }

    return list.map(value => ({
      label: t(`service.subCategory.${camelCase(value)}`),
      value
    }));
  }, [category, t]);

  if (!category || !options?.length) {
    return null;
  }

  return (
    <SelectField
      {...selectProps}
      control={control}
      options={options}
    />
  );
}

/**
 * `DynamicServiceSelect` component.
 */

export function DynamicServiceSelect(props: DynamicServiceSelectProps) {
  const { control, groupedServices, ...selectProps } = props;
  const subCategory = useWatch({
    control,
    name: 'serviceSubCategory'
  }) as Category;

  const options = useMemo(() => {
    const list =
      groupedServices?.[snakeCase(subCategory) as keyof ServicesBySubcategory];

    if (!list?.length) {
      return undefined;
    }

    return list.map(({ slug, title }) => ({ label: title, value: slug }));
  }, [groupedServices, subCategory]);

  if (!subCategory || !options?.length) {
    return null;
  }

  return (
    <SelectField
      {...selectProps}
      control={control}
      options={options}
    />
  );
}

/**
 * `BookAppointment` page.
 */

const BookAppointment: NextPage = () => {
  const { previousRoute } = useRouterHistory();
  const { locale } = useRouter();
  const { t } = useTranslation('appointment');
  const [service, setService] = useState<Service['slug'] | undefined>();
  const groupedServices = useAsync(() => {
    return getServicesBySubcategory(locale as Locale);
  }, [locale]);

  const serviceState = useAsync(() => {
    if (!service) {
      return Promise.resolve(undefined);
    }

    return getService(service, locale as Locale);
  }, [service]);

  const onSubmit = useCallback(
    async (formData: AppointmentFormData, reset: () => void) => {
      const services = groupedServices?.result?.[formData.serviceSubCategory];
      const service = services?.find(
        ({ slug }) => slug === formData.serviceId
      ) as Service;

      try {
        const response = await postAppointment({
          ...formData,
          serviceId: service?.id
        });

        toast.success(t('network.success'));

        reset();

        return response;
      } catch (error) {
        toast.error(t('network.error'));

        return error;
      }
    },
    [groupedServices, t]
  );

  return (
    <>
      <SEO title={t('title')} />

      <TriggerNavbarTheme theme={'light'} />

      <BookAppointmentForm
        modalCloseButtonLink={previousRoute}
        onSubmit={onSubmit}
      >
        {({ control, setValue }) => (
          <FieldsGrid>
            <SelectField
              control={control}
              isRequired
              label={t('common:form.labels.category')}
              name={'serviceCategory'}
              onChange={() => {
                setValue('serviceSubCategory', '' as any);
                setValue('serviceId', '');
                setValue('staffMemberId', '');
                setService(undefined);
              }}
              options={Object.keys(serviceCategoryEnum).map(value => ({
                label: t(`common:service.category.${value}`),
                value
              }))}
              rules={requiredRule(t)}
            />

            <DynamicSubCategorySelect
              control={control}
              isLoading={groupedServices?.loading}
              isRequired
              label={t('common:form.labels.subCategory')}
              name={'serviceSubCategory'}
              onChange={() => {
                setValue('serviceId', '');
                setValue('staffMemberId', '');
                setService(undefined);
              }}
              rules={requiredRule(t)}
            />

            <DynamicServiceSelect
              control={control as any}
              groupedServices={groupedServices?.result}
              isLoading={serviceState?.loading}
              isRequired
              label={t('common:form.labels.service')}
              name={'serviceId'}
              onChange={(event: any) => {
                if (event?.target?.value) {
                  setService(event?.target?.value);
                }
              }}
              rules={requiredRule(t)}
            />
          </FieldsGrid>
        )}
      </BookAppointmentForm>
    </>
  );
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<SSGProps> = async ({ locale }) => {
  return {
    props: {
      hideFooter: true,
      hideNavigation: true,
      navbarTheme: 'light',
      ...(await serverSideTranslations(locale as Locale, [
        'common',
        'appointment'
      ]))
    },
    revalidate: 60
  };
};

/**
 * Export `BookAppointment` component.
 */

export default BookAppointment;
